import { Controller } from "@hotwired/stimulus";

export default class extends Controller {

    static targets = ["content", "inner", "button"];
    static values = {
        collapsedLines: { type: Number, default: 6 },
        accordion: { type: Boolean, default: true },
        readMoreText: { type: String, default: "Skaityti daugiau" },
        readLessText: { type: String, default: "Skaityti mažiau" }
    };

    connect() {
        this._scopeEl = this.element.closest(".swiper") || this.element.parentElement;

        this.element.classList.add("is-collapsed");
        this.element.style.setProperty("--rm-lines", this.collapsedLinesValue);

        this._applyCollapsedHeight();
        this._updateOverflowUI();

        this._onOpen = (e) => {
            if (!this.accordionValue) return;
            if (e.detail === this.element) return;
            this.collapse();
        };
        this._scopeEl?.addEventListener("read-more:open", this._onOpen);

        this._onResize = () => {
            if (this.isExpanded()) {
                this.contentTarget.style.height = "auto";
            } else {
                this._applyCollapsedHeight();
            }
            this._updateOverflowUI();
        };
        window.addEventListener("resize", this._onResize, { passive: true });
    }

    disconnect() {
        this._scopeEl?.removeEventListener("read-more:open", this._onOpen);
        window.removeEventListener("resize", this._onResize);
    }

    toggle() {
        this.isExpanded() ? this.collapse() : this.expand();
    }

    expand() {
        this._scopeEl?.dispatchEvent(
            new CustomEvent("read-more:open", { bubbles: false, detail: this.element })
        );

        this.element.classList.remove("is-collapsed");
        this.element.classList.add("is-expanding");

        const from = this._collapsedHeightPx();
        const to = this._expandedHeightPx();

        this.contentTarget.style.height = `${from}px`;

        requestAnimationFrame(() => {
            this.contentTarget.style.height = `${to}px`;

            this.element.classList.add("is-expanded");
            this.buttonTarget.textContent = this.readLessTextValue;
            this.buttonTarget.setAttribute("aria-expanded", "true");

            const onEnd = (e) => {
                if (e.target !== this.contentTarget) return;
                this.contentTarget.removeEventListener("transitionend", onEnd);

                this.contentTarget.style.height = "auto";
                this.element.classList.remove("is-expanding");

                this._notifySwiperUpdate();
            };

            this.contentTarget.addEventListener("transitionend", onEnd);
        });
    }

    collapse() {
        if (!this.isExpanded() && !this.element.classList.contains("is-expanding")) return;

        const current = this.contentTarget.getBoundingClientRect().height;
        this.contentTarget.style.height = `${Math.ceil(current)}px`;

        requestAnimationFrame(() => {
            this.element.classList.remove("is-expanded");
            this.element.classList.add("is-collapsed");

            const to = this._collapsedHeightPx();
            this.contentTarget.style.height = `${to}px`;

            this.buttonTarget.textContent = this.readMoreTextValue;
            this.buttonTarget.setAttribute("aria-expanded", "false");

            const onEnd = (e) => {
                if (e.target !== this.contentTarget) return;
                this.contentTarget.removeEventListener("transitionend", onEnd);

                this._updateOverflowUI();
                this._notifySwiperUpdate();
            };

            this.contentTarget.addEventListener("transitionend", onEnd);
        });
    }

    isExpanded() {
        return this.element.classList.contains("is-expanded");
    }

    _applyCollapsedHeight() {
        this.contentTarget.style.height = `${this._collapsedHeightPx()}px`;
    }

    _collapsedHeightPx() {
        const styles = getComputedStyle(this.innerTarget);
        let lineHeight = parseFloat(styles.lineHeight);

        if (!Number.isFinite(lineHeight)) {
            const fontSize = parseFloat(styles.fontSize) || 14;
            lineHeight = fontSize * 1.2;
        }

        return Math.ceil(lineHeight * this.collapsedLinesValue + 1);
    }

    _expandedHeightPx() {
        this.element.classList.add("rm--measuring");
        const h = Math.ceil(this.innerTarget.scrollHeight);
        this.element.classList.remove("rm--measuring");
        return h;
    }

    _updateOverflowUI() {
        const full = this._expandedHeightPx();
        const collapsed = this._collapsedHeightPx();
        const hasOverflow = full > collapsed + 2;

        this.element.classList.toggle("has-overflow", hasOverflow);

        if (!hasOverflow) {
            this.element.classList.remove("is-expanded");
            this.element.classList.add("is-collapsed");
            this.buttonTarget.setAttribute("aria-expanded", "false");
            this.buttonTarget.textContent = this.readMoreTextValue;
            this._applyCollapsedHeight();
        }
    }

    _notifySwiperUpdate() {
        const swiperEl = this.element.closest(".swiper");
        if (!swiperEl) return;

        swiperEl.dispatchEvent(new CustomEvent("read-more:changed", { bubbles: true }));
    }
}
